import { Controller } from '@hotwired/stimulus';
import Choices from 'choices.js';
import multiMeetingStyleOutletController from 'controllers/multi_meeting_style_select_controller';

type choiceData = { label: string; value: string; selected: string | boolean; customProperties: string };

/**
 * 担当者 UI に関する制御を行うコントローラ
 *
 * ### パラメータについて
 *
 * - meetingStyleValue : 会議形式(シングル)
 *   - 会議形式を変更した際に meeting_style_controller から直接変更される
 *   - 担当者リストの中で、選択された会議形式に対応可能な人だけ enable になる
 * - userAttendStyleValue : 担当者のアサイン方式
 *   - アサイン方式を変更した際に user_attend_style_controller から直接変更される
 *   - ビデオ会議 URL を発行する代表者設定 UI の表示をコントロールする
 *   - 全員出席の場合のみ、代表者の設定が必要
 * - meetingStyleSettingValue : 会議選択設定(シングル、マルチ)
 *   - 会議選択設定を変更した際に meeting_style_setting_controller から直接変更される
 *   - 担当者リストの中で、選択された設定内で選択された会議形式すべてに対応可能な人だけ enable になる
 * - multiMeetingStyleValue : 会議形式(マルチ)
 *   - 会議形式を変更した際に multi_meeting_style_select_controller から直接変更される
 *   - 担当者リストの中で、選択された会議形式すべてに対応可能な人だけ enable になる
 */
export default class extends Controller {
  static values = {
    meetingStyle: String,
    userAttendStyle: String,
    meetingStyleSetting: String,
    multiMeetingStyle: Array<string>,
  };

  static targets = [
    'user',
    'userDateList',
    'issuerBox',
    'issuerSelector',
    'userSelect',
    'container',
    'template',
    'userPrioritySelect',
  ];
  static outlets = ['multi-meeting-style-select'];
  declare multiMeetingStyleSelectOutlet: multiMeetingStyleOutletController;
  declare hasMultiMeetingStyleSelectOutlet: boolean;

  declare userTarget: HTMLSelectElement;
  declare userDateListTarget: HTMLDataListElement;
  declare userChoiceObj: Choices;
  declare issuerBoxTarget: HTMLElement;
  declare issuerSelectorTarget: HTMLSelectElement;
  declare issuerChoiceObj: Choices;
  declare userSelectTargets: HTMLSelectElement[];
  declare containerTarget: HTMLElement;
  declare templateTarget: HTMLTemplateElement;
  declare userPrioritySelectTargets: HTMLSelectElement[];
  declare meetingStyleSettingValue: 'single' | 'multi';
  declare meetingStyleValue: 'offline' | 'google_meet' | 'zoom' | 'teams' | 'other' | 'tel' | 'notice';
  declare userAttendStyleValue: 'all_attend' | 'random_single_attend';
  declare multiMeetingStyleValue: string[];
  private beforeChangeIssuerValue: string;

  private tooltip_text_web =
    '現在は選択することができません。切り替えるためには、別のメンバーをチームに招待して、その人自身が「アカウント設定画面」にて、Web会議ツールの連携作業を行う必要があります。';
  private tooltip_text_offline =
    '現在は選択することができません。切り替えるためには、別のメンバーをチームに招待する必要があります。';
  private userChoiceObjs: Choices[] = [];

  initialize(): void {
    this.initIssuerChoices();
    this.updateIssuerSelector();
    this.searchOffChoicesInput();
    this.beforeChangeIssuerValue = this.issuerChoiceObj.getValue(true) as string;
    this.initializeSelectedOptions();
    this.hideVideochatUrlIssueToUserSelects();
  }

  connect(): void {
    // 担当者（同席者）の change イベントを走らせる（強制バリデート）（「再読み込み」でもイベントが発火するように setTimeout を使用）
    // 最初の要素が空の場合のみに絞る（担当者が多いと読み込みに時間がかかるため）
    if (this.userSelectTargets[0].value === '') {
      setTimeout(() => {
        this.userSelectTargets.forEach((select) => {
          select.dispatchEvent(new Event('change'));
        });
      }, 0);
    }

    // 必ず選択肢を反映（initialize の後でも再実行）
    this.updateSelections();
    this.reset();
  }

  /**
   * 既に担当者に設定されている人を最初から選択された状態にする
   */
  initializeSelectedOptions(): void {
    // 担当者に選択できるユーザがいない場合、選択肢には何も表示されないが select を1つだけ表示する（バリデーションエラーを表示する都合上）
    if (this.userDateListTarget.options.length === 0) {
      const nodes = Array.from(this.templateTarget.content.cloneNode(true).childNodes);
      const userSelectDivs = nodes.filter(
        (node) => node instanceof HTMLElement && node.classList.contains('user_select'),
      );

      const userSelectDiv = userSelectDivs[0];
      const select = (userSelectDiv as Element).querySelector('select') as HTMLSelectElement;

      this.containerTarget.appendChild(userSelectDiv);
      this.initUserChoices(select);

      // バリデーションエラーを表示するために change イベントを発火
      this.userSelectTargets.forEach((select) => {
        select.dispatchEvent(new Event('change'));
      });

      return;
    }

    const selectedOptions = Array.from(this.userDateListTarget.options)
      .filter((opt) => opt.dataset.selected === 'true')
      .map((opt) => ({
        value: opt.dataset.value,
        priority: opt.dataset.priority ? Number(opt.dataset.priority) : 1,
        firstCreatedAt: opt.dataset.firstCreatedAt,
      }))
      .sort((a, b) => {
        // 優先度で高い順に並べる
        const priorityDiff = b.priority - a.priority;
        if (priorityDiff !== 0) {
          return priorityDiff;
        }
        // 同じ優先度内ではブックボードへの追加日時順に並べる
        const dateA = a.firstCreatedAt ? new Date(a.firstCreatedAt).getTime() : 0;
        const dateB = b.firstCreatedAt ? new Date(b.firstCreatedAt).getTime() : 0;
        return dateA - dateB;
      });

    // 自分にライセンスがない && 新規ブックボード作成したとき、誰も選択されていない状態になる
    // 選択可能なユーザが issuerSelector に入るので、このユーザを選択したことにする
    if (selectedOptions.length === 0) {
      this.addVideochatUrlIssueToUserSelects();
    }

    const nodes = Array.from(this.templateTarget.content.cloneNode(true).childNodes) as HTMLElement[];
    const userSelectDivs = nodes.filter(
      (node) => node instanceof HTMLElement && node.classList.contains('user_select'),
    );

    selectedOptions.forEach((userIds, index) => {
      const userSelectDiv = userSelectDivs[index];
      const select = userSelectDiv.querySelector('select')!;

      this.containerTarget.appendChild(userSelectDiv);
      this.initUserChoices(select, userIds.value);
    });

    // 担当者と同じ順で優先度も並び替える
    this.userSelectTargets.forEach((select, index) => {
      const correspondingOption = selectedOptions[index];
      if (correspondingOption) {
        const prioritySelect = select
          .closest('.user_select')
          ?.querySelector('[data-book-board-users-target="userPrioritySelect"]') as HTMLSelectElement;
        if (prioritySelect) {
          prioritySelect.value = correspondingOption.priority.toString();
        }
      }
    });
  }

  /**
   * ユーザリストと URL 発行者リストを更新する
   * 画面初期表示時、ミーティングスタイル変更時、担当者の割り当て方法変更時
   */
  reset(): void {
    this.updateList();
    this.updateIssuerSelector();
    this.resetCalendarUser();
  }

  add(): void {
    const nodes = Array.from(this.templateTarget.content.cloneNode(true).childNodes) as HTMLElement[];
    const userSelectDivs = nodes.filter(
      (node) => node instanceof HTMLElement && node.classList.contains('user_select'),
    );
    const node = userSelectDivs[0];
    const select = node.querySelector('select')!;
    const prioritySelect = node.querySelectorAll('select')[1];
    const options = Array.from(node.querySelectorAll('option'));

    // 初期状態の優先度は「低」
    const defaultOption = options.find((option) => option.dataset.default === 'true');
    if (defaultOption) {
      defaultOption.selected = true;
    }

    this.containerTarget.appendChild(node);
    this.initUserChoices(select);
    this.updateSelections(); // 重複排除更新
    this.addedSelectName(select, prioritySelect);
    this.updateList();
    this.hideUserItem();
  }

  remove(event: Event): void {
    if (this.userSelectTargets.length === 1) {
      return;
    } // 最低1つは残す

    const row = (event.currentTarget as HTMLElement).closest('[data-user-row]');
    const select = row?.querySelector('select');
    row?.remove();

    // 残りの userSelect の name を再設定
    this.userSelectTargets.forEach((select, index) => {
      const prioritySelect = select
        .closest('.user_select')
        ?.querySelector('[data-book-board-users-target="userPrioritySelect"]') as HTMLSelectElement;
      const hiddenInput = select.closest('.user_select')?.querySelector('input[type="hidden"]') as HTMLSelectElement;

      select.name = `book_board_setting[book_board_users_attributes][${index}][user_id]`;
      prioritySelect.name = `book_board_setting[book_board_users_attributes][${index}][priority]`;
      hiddenInput.name = `book_board_setting[book_board_users_attributes][${index}][id]`;
    });

    if (select) {
      this.userChoiceObjs = this.userChoiceObjs.filter((obj) => obj.passedElement.element !== select);
      // バリデーションを実行するために change イベントを発火
      // 最初の要素が空の場合のみに絞る（担当者が多いと読み込みに時間がかかるため）
      if (this.userSelectTargets[0].value === '') {
        setTimeout(() => {
          this.userSelectTargets.forEach((select) => {
            select.dispatchEvent(new Event('change'));
          });
        }, 250);
      }
    }
    this.updateSelections();
    this.reset();
  }

  /**
   * 選択肢の更新
   */
  updateSelections(): void {
    const selectedValues = this.userSelectTargets.map((s: HTMLSelectElement) => s.value).filter((v) => v !== '');

    this.userChoiceObjs.forEach((choiceObj, index) => {
      const currentValue = this.userSelectTargets[index].value;
      const options = this.userList().map((user) => {
        const disabled = selectedValues.includes(user.value) && user.value !== currentValue;
        return {
          value: user.value,
          label: user.label,
          selected: user.value === currentValue,
          disabled,
          customProperties: user.customProperties,
        };
      });
      choiceObj.clearChoices();
      choiceObj.setChoices(options, 'value', 'label', false);
    });
  }

  /**
   * ミーティングスタイルセッティングが変更時のリセット
   *
   * @param meetingStyleValue
   */
  resetMeetingStyleSetting(meetingStyleSettingValue): void {
    this.meetingStyleSettingValue = meetingStyleSettingValue;
    this.reset();

    // バリデーションを実行するために change イベントを発火
    // 最初の要素が空の場合のみに絞る（担当者が多いと読み込みに時間がかかるため）
    if (this.userSelectTargets[0].value === '') {
      setTimeout(() => {
        this.userSelectTargets.forEach((select) => {
          select.dispatchEvent(new Event('change'));
        });
      }, 250);
    }
  }

  /**
   * ミーティングスタイルが変更時のリセット
   *
   * @param meetingStyleValue
   */
  resetMeetingStyle(meetingStyleValue): void {
    this.meetingStyleValue = meetingStyleValue;
    this.reset();

    // バリデーションを実行するために change イベントを発火
    // 最初の要素が空の場合のみに絞る（担当者が多いと読み込みに時間がかかるため）
    if (this.userSelectTargets[0].value === '') {
      setTimeout(() => {
        this.userSelectTargets.forEach((select) => {
          select.dispatchEvent(new Event('change'));
        });
      }, 250);
    }
  }

  /**
   * マルチミーティングスタイル変更時のリセット
   *
   * @param meetingStyleValue
   */
  resetMultiMeetingStyle(multiMeetingStyleValue: string[]): void {
    this.multiMeetingStyleValue = multiMeetingStyleValue;
    this.reset();

    // バリデーションを実行するために change イベントを発火
    // 最初の要素が空の場合のみに絞る（担当者が多いと読み込みに時間がかかるため）
    if (this.userSelectTargets[0].value === '') {
      setTimeout(() => {
        this.userSelectTargets.forEach((select) => {
          select.dispatchEvent(new Event('change'));
        });
      }, 250);
    }
  }

  /**
   * 「全員が参加」「ランダム」「優先度順」切り替え時のリセット
   *
   * @param userAttendStyleValue
   */
  resetUserAttendStyleValue(userAttendStyleValue): void {
    this.userAttendStyleValue = userAttendStyleValue;
  }

  /**
   * ビデオチャット URL の発行者を決める <select> の選択肢を更新
   * この発行者は「全員を担当者にする場合」のみ機能する
   */
  updateIssuerSelector(): void {
    // 現在の selected の値を抽出
    const selectedValue = this.issuerChoiceObj.getValue(true) as string;
    const issuerSelectorChoicesList = this.userList(true);

    for (let i = 0; i < issuerSelectorChoicesList.length; i++) {
      // selected が空の場合は先頭を selected にしてループを抜ける
      if (!selectedValue) {
        issuerSelectorChoicesList[i].selected = true;
        break;
      }
      issuerSelectorChoicesList[i].selected =
        selectedValue == issuerSelectorChoicesList[i].value || issuerSelectorChoicesList.length == 1;
    }

    this.issuerChoiceObj.clearStore(); // リストデータを全て空にする
    this.issuerChoiceObj.setChoices(issuerSelectorChoicesList, 'value', 'label', false); // リストデータを再設定
    this.hideUserItem();

    if (this.userSelectTargets[0]) {
      this.issuerSelectorTarget.dispatchEvent(new Event('change')); // バリデートを走らせるための dispach
    }
  }

  /**
   * 選択できないユーザを選択肢のリストから削除（※見た目上）
   * 見た目上にしている理由： サーバー側の処理（通知等）を複雑化させたくない為
   */
  hideUserItem(): void {
    const issuerSelectedValue = this.issuerChoiceObj.getValue(true) as string;
    const choicesItems = document.getElementById('book_board_users').getElementsByClassName('choices__item');

    for (let i = 0; i < choicesItems.length; i++) {
      const choicesItem = choicesItems[i] as HTMLElement;
      const closestChoicesInner = choicesItem
        .closest('.choices')
        .querySelector('[data-book-board-users-target="issuerSelector"]');
      choicesItem.classList.remove('hide');

      if ((document.querySelector('input[value="all_attend"]') as HTMLInputElement).checked && closestChoicesInner) {
        // 全員が参加のとき、ホストの選択肢には今ホストになっている人以外全てのユーザを表示
        if (issuerSelectedValue == choicesItem.dataset?.value) {
          choicesItem.classList.add('hide');
        }
      } else {
        // 同席者または誰か一名が参加のとき、既に選択された全ユーザを候補から除外
        if (this.userSelected(choicesItem.dataset.value)) {
          choicesItem.classList.add('hide');
        }
      }
    }
  }

  /**
   * 「その他メンバーの連携カレンダーに予定を自動登録」ドロップダウンリスト変更の為のカスタムイベントを発火
   */
  resetCalendarUser(): void {
    const customEvent = new CustomEvent('bookBoardUserUpdateList');
    window.dispatchEvent(customEvent);
  }

  allClear(event: InputEvent): void {
    const target = event.target as HTMLInputElement;
    const elem = document.getElementsByClassName('choices__inner')[1];
    if (target.checked) {
      this.userList().map((user) => {
        this.userChoiceObj.removeActiveItems(Number(user.value));
      });
      this.userChoiceObj.disable();
      elem.classList.add('choices_disabled');
    } else {
      this.userChoiceObj.enable();
      elem.classList.remove('choices_disabled');
    }
  }

  /**
   * 担当者(ホスト)に選ばれているユーザー全員が選択可能でない場所名の配列を生成し、multi-meeting-style-selectのupdateDisabledOptionの引数として渡して実行する
   * 例) Zoomと未連携のユーザーが担当者として選択されている場合、['zoom']という配列をupdateDisabledOptionに渡して実行する
   */
  setAvailableMeetingStyles(): void {
    if (!this.hasMultiMeetingStyleSelectOutlet) return;
    let selectedAttendees;
    let availableMeetingStyles;
    const allMeetingStyles = ['offline', 'google_meet', 'zoom', 'teams', 'other', 'tel', 'notice'];

    if ((document.querySelector('input[value="all_attend"]') as HTMLInputElement).checked) {
      selectedAttendees = this.issuerSelectorTarget;

      availableMeetingStyles = Array.from(selectedAttendees.options).map((option) => {
        const parsedItem = JSON.parse((option as HTMLElement).dataset.customProperties);
        return parsedItem.availableMettingStyles.split(' ');
      });
    } else {
      selectedAttendees = this.userSelectTargets;

      availableMeetingStyles = selectedAttendees.map((select) => {
        const option = select.options[select.selectedIndex];
        const parsedItem = option ? JSON.parse(option.dataset.customProperties) : null;
        return parsedItem ? parsedItem.availableMettingStyles.split(' ') : null;
      });
    }

    let disabledItems;
    if (availableMeetingStyles != '') {
      disabledItems = allMeetingStyles.filter((meeting_style) => {
        return !availableMeetingStyles
          .filter((array) => array !== null)
          .every((array) => array.includes(meeting_style));
      });
    } else {
      // 担当者が誰も選択されていない場合は全てのミーティングスタイルを無効化
      disabledItems = allMeetingStyles;
    }

    this.multiMeetingStyleSelectOutlet.updateDisabledOption(disabledItems);
  }

  /**
   * 担当者の参加方式を変更したときの、userSelectTargets で行う処理
   */
  resetSelectWhenUserAttendStyleChanged(): void {
    if (this.userSelectTargets[0].value === '') {
      setTimeout(() => {
        this.userSelectTargets.forEach((select) => {
          select.dispatchEvent(new Event('change'));
        });
      }, 0);
    } else {
      this.updateSelections();
      this.reset();
    }
  }

  /**
   * 誰も担当者が選択されていないとき、入力必須にすることでバリデーションエラーを表示する
   * 一人でも担当者が選択されている場合は必須を解除する
   */
  changeRequired(): void {
    const canShowError =
      !this.userSelectTargets.some((select) => select.value !== '') &&
      !(document.querySelector('input[value="all_attend"]') as HTMLInputElement).checked;

    if (canShowError) {
      this.userSelectTargets[0].setAttribute('required', 'true');
    } else {
      this.userSelectTargets.forEach((select) => select.removeAttribute('required'));
    }
  }

  /**
   * 追加されたての select に name を設定
   *
   * @param select
   * @param prioritySelect
   * @private
   */
  private addedSelectName(select, prioritySelect): void {
    const elements = document.querySelectorAll(
      '[id^="book_board_setting_book_board_users_attributes_"][id$="_user_id"]',
    );
    const hiddenInput = select.closest('.user_select')?.querySelector('input[type="hidden"]') as HTMLInputElement;
    let name_num;

    if (elements.length === 1) {
      name_num = 0; // 追加したselectが1つ目の場合
    } else {
      const lastElement = (elements[elements.length - 2] as HTMLInputElement).name;
      const match = lastElement.match(/\[(\d+)\]/);
      name_num = Number(match[1]) + 1;
    }

    select.name = `book_board_setting[book_board_users_attributes][${name_num}][user_id]`;
    prioritySelect.name = `book_board_setting[book_board_users_attributes][${name_num}][priority]`;
    hiddenInput.name = `book_board_setting[book_board_users_attributes][${name_num}][id]`;
    hiddenInput.value = '';
  }

  /**
   * 全員が参加時の担当者（ホスト）を選択した際、担当者（同席者）の担当者一覧に加える
   *
   * 同席者のリストにあるユーザが book_board_users に反映されるため追加しています
   * 追加した上で非表示にするので見た目上変化はないです
   *
   */
  addVideochatUrlIssueToUserSelects(): void {
    const meeting_style_support = ['random_single_attend', 'ordered_single_attend'].includes(this.userAttendStyleValue);
    const userList = this.userList(meeting_style_support);
    const issuerUserId = this.issuerSelectorTarget.value;
    const userSelectValues = this.userSelectTargets.map((select) => select.value);

    // ホストに選択したユーザが同席者の担当者一覧にいなかった場合、同席者の担当者一覧に追加する
    // 既に同席者として選択されていたユーザをホストに変更した場合は何もしない
    if (
      (document.querySelector('input[value="all_attend"]') as HTMLInputElement).checked &&
      !userSelectValues.includes(issuerUserId)
    ) {
      this.add();

      const lastChoiceObj = this.userChoiceObjs[this.userChoiceObjs.length - 1];
      const lastSelect = this.userSelectTargets[this.userSelectTargets.length - 1];
      const updatedOptions = userList.map((option) => {
        return {
          ...option,
          selected: option.value === issuerUserId,
        };
      });

      lastChoiceObj.clearStore();
      lastChoiceObj.setChoices(updatedOptions, 'value', 'label', false);
      lastSelect.value = issuerUserId;
    }

    // 元々ホストだったユーザは選択から外す
    // 上記のパターンに加え、同席者として選択されていたユーザをホストに変更した場合も該当
    // ※ この処理は必ず上記の追加処理を行った後に実行すること（最後の一人は解除できないようにしてあるため）
    if (
      (document.querySelector('input[value="all_attend"]') as HTMLInputElement).checked &&
      (!userSelectValues.includes(issuerUserId) || issuerUserId != this.beforeChangeIssuerValue)
    ) {
      this.userSelectTargets.forEach((select) => {
        if (select.value === this.beforeChangeIssuerValue) {
          const removeButton = select.closest('.user_select')?.querySelector('button') as HTMLButtonElement;
          removeButton.click();
        }
      });
    }

    // ホストを同席者の担当者一覧に追加する処理を行った場合、選択肢も更新
    if (
      (document.querySelector('input[value="all_attend"]') as HTMLInputElement).checked &&
      !userSelectValues.includes(issuerUserId)
    ) {
      this.updateSelections();
      this.reset();
    }

    // 担当者（ホスト）で選択されているユーザーを担当者（同席者）の担当者一覧から非表示にする
    if ((document.querySelector('input[value="all_attend"]') as HTMLInputElement).checked) {
      this.hideVideochatUrlIssueToUserSelects();
    }
  }

  // 担当者（ホスト）で選択されているユーザーを担当者（同席者）の担当者一覧から非表示にする
  hideVideochatUrlIssueToUserSelects(): void {
    const issuerSelectedValue = this.issuerChoiceObj.getValue(true) as string;
    const elements = this.userSelectTargets;

    for (let i = 0; i < elements.length; i++) {
      const element = elements[i] as HTMLElement;
      const userSelectDiv = element.closest('.user_select');

      if (!userSelectDiv) continue;

      userSelectDiv.classList.remove('hide');

      if (
        (document.querySelector('input[value="all_attend"]') as HTMLInputElement).checked &&
        issuerSelectedValue == (element as HTMLInputElement).value
      ) {
        userSelectDiv.classList.add('hide');
      }
    }
  }

  updateBeforeChangeIssuerValue(): void {
    this.beforeChangeIssuerValue = this.issuerChoiceObj.getValue(true) as string;
  }

  /**
   * ユーザリストを更新する
   *
   * ひとりを担当者にする形式の場合、選択したミーティング形式をサポートしたユーザのみが表示される
   * 例えば Zoom の場合は、Zoom 連携をしているユーザのみが表示される
   * また「ひとりを担当者にする」に切り替えの際、Choices.js 内部でミーティング形式をサポートしていないユーザーがリストから消されてしまう仕様の為
   * 「全員を担当者」に戻した場合に Choices.js のリストから消されたユーザーは Choices.js 化後のエレメントから拾えなくなくなってしまう・・
   * ので、元データとして用意した datalist のデータで再初期化、消えてしまったミーティング形式をサポートしていないユーザーを再選択可能にしています
   *
   * @private
   */
  private updateList() {
    const meeting_style_support = !(document.querySelector('input[value="all_attend"]') as HTMLInputElement).checked;
    const userList = this.userList(meeting_style_support);

    this.userChoiceObjs.forEach((choiceObj, index) => {
      const currentValue = this.userSelectTargets[index]?.value || '';
      const updatedOptions = userList.map((option) => {
        return {
          ...option,
          selected: option.value === currentValue,
        };
      });

      choiceObj.clearStore();
      choiceObj.setChoices(updatedOptions, 'value', 'label', false);
    });
  }

  /**
   * ユーザリストの selected 返す
   *
   * @param optionValue
   * @private
   */
  private userSelected(optionValue): boolean {
    // 既に selected のユーザー
    let selectedValues = this.userChoiceObjs.map((choiceObj) => choiceObj.getValue(true) as string[]).flat();

    // 担当者の割当方法「全員を担当者にする」が選択されている場合
    if ((document.querySelector('input[value="all_attend"]') as HTMLInputElement).checked) {
      selectedValues = selectedValues.filter((value) => {
        // 変更前の担当者（ホスト）に含まれる selected は除外
        return this.beforeChangeIssuerValue != value;
      });

      const issuerSelectedValue = this.issuerChoiceObj.getValue(true) as string; // 担当者（ホスト）で selected のユーザー
      return selectedValues.includes(optionValue) || issuerSelectedValue == optionValue;
    } else {
      return selectedValues.includes(optionValue);
    }
  }

  /**
   * Choices.js 用のオリジンデータからユーザーリストを返す
   *
   * @param meeting_style_support
   * @private
   */
  private userList(meeting_style_support = false): choiceData[] {
    const options = meeting_style_support
      ? Array.from(this.userDateListTarget.options).filter((option) => {
          const styles = option.dataset.availableMettingStyles.split(' ');
          return this.checkStyleInclude(styles);
        })
      : Array.from(this.userDateListTarget.options);
    return options.map((option) => {
      return {
        label: option.dataset.label,
        value: option.dataset.value,
        selected: option.dataset.selected == 'true',
        customProperties: JSON.stringify({
          availableMettingStyles: option.dataset.availableMettingStyles,
          iconUrl: option.dataset.iconUrl,
        }),
      };
    });
  }

  /**
   * Choices.js で初期化後のエレメントからミーティングスタイルをサポートしているユーザーリストを返す
   *
   * @private
   */
  private mettingStyleSupportUserList(): HTMLOptionElement[] {
    const selectedOptions = Array.from(this.userTarget.options);
    return selectedOptions.filter((option) => {
      const customProperties = JSON.parse(option.dataset.customProperties);
      const availableMettingStyles = customProperties.availableMettingStyles;
      const styles = availableMettingStyles.split(' ');
      return this.checkStyleInclude(styles);
    });
  }

  /**
   * 担当者(ホスト) <select> に Choices.js ライブラリを適用
   *
   * @private
   */
  private initIssuerChoices(): void {
    this.issuerChoiceObj = new Choices(this.issuerSelectorTarget, {
      searchEnabled: false,
      searchChoices: false,
      removeItemButton: false,
      shouldSort: true,
      shouldSortItems: true,
      sorter: function (a, b) {
        return a.value - b.value;
      },
      placeholder: true,
      noChoicesText: '他に登録先の候補がありません',
      placeholderValue: 'メンバーを選択       ', // 文字数*バイト数で表示widthが決められていそうなので空白を付与している
      itemSelectText: '',
      // カスタムテンプレートの指定
      callbackOnCreateTemplates: function (template) {
        return {
          item: ({ classNames }, data) => {
            return template(`
              <div class="${classNames.item}" data-item data-id="${data.id}" data-value="${data.value}">
                ${
                  Object.keys(data.customProperties).length != 0 // 画面初期表示時には iconUrl が取れないので一旦ブランクを挿入
                    ? '<img width="24" height="24" class="user__icon" src="' +
                      JSON.parse(data.customProperties)?.iconUrl +
                      '" />'
                    : ''
                } ${data.label}
              </div>
            `);
          },
          choice: ({ classNames }, data) => {
            return template(`
              <div class="${classNames.item} ${classNames.itemChoice} ${classNames.itemSelectable}" data-choice data-choice-selectable data-id="${data.id}" data-value="${data.value}">
                ${
                  Object.keys(data.customProperties).length != 0 // 画面初期表示時には iconUrl が取れないので一旦ブランクを挿入
                    ? '<span><img width="24" height="24" class="user__icon" src="' +
                      JSON.parse(data.customProperties)?.iconUrl +
                      '" /></span>'
                    : ''
                } ${data.label}
              </div>
            `);
          },
        };
      },
    });
  }

  /**
   * 担当者(同席者) <select> に Choices.js ライブラリを適用
   * see: https://github.com/Choices-js/Choices/blob/master/src/scripts/templates.ts
   *
   * @private
   */
  private initUserChoices(select: HTMLSelectElement, initialValue: string | null = null): void {
    const choicesInstance = new Choices(select, {
      searchEnabled: false,
      removeItemButton: false,
      placeholder: true,
      noChoicesText: '他に登録先の候補がありません',
      callbackOnCreateTemplates: (template) => {
        return {
          item: ({ classNames }, data) => {
            const iconUrl = JSON.parse(data.customProperties || '{}')?.iconUrl;
            return template(`
              <div class="${classNames.item}" data-item data-id="${data.id}" data-value="${data.value}">
                ${iconUrl ? `<img width="24" height="24" class="user__icon" src="${iconUrl}" />` : ''} ${data.label}
              </div>
            `);
          },
          choice: ({ classNames }, data) => {
            const iconUrl = JSON.parse(data.customProperties || '{}')?.iconUrl;
            return template(`
              <div class="${classNames.item} ${classNames.itemChoice}" data-choice data-id="${data.id}" data-value="${data.value}">
                ${iconUrl ? `<img width="24" height="24" class="user__icon" src="${iconUrl}" />` : ''} ${data.label}
              </div>
            `);
          },
        };
      },
    });
    this.userChoiceObjs.push(choicesInstance);

    const options = this.userList().map((user) => {
      return {
        value: user.value,
        label: user.label,
        selected: user.value === initialValue,
        disabled: false,
        customProperties: user.customProperties,
      };
    });
    choicesInstance.setChoices(options, 'value', 'label', false);
  }

  /**
   * Choices.js で生成された検索用の input を readonly にする
   *
   * @private
   */
  private searchOffChoicesInput(): void {
    const elements = document.querySelectorAll('input[type="search"]');
    elements.forEach((element: HTMLInputElement) => {
      element.readOnly = true;
    });
  }

  /**
   * 該当ユーザが担当者になれるかどうかを判定
   *
   * @param styles
   * @private
   */
  private checkStyleInclude(styles): boolean {
    if (this.meetingStyleSettingValue === 'multi') {
      if (!this.multiMeetingStyleValue.length) return false;
      const isInclude = this.multiMeetingStyleValue.every((style) => styles.includes(style));

      return isInclude;
    }

    return styles.includes(this.meetingStyleValue);
  }
}
